import { useState, useEffect, useCallback, useRef } from 'react';
import { Spin, notification } from 'antd';
import {
  BookOutlined, CalendarOutlined, CheckCircleOutlined, CloseCircleOutlined,
  CloudUploadOutlined, DownloadOutlined, EnvironmentOutlined, EyeOutlined,
  FileTextOutlined, InfoCircleOutlined, LoadingOutlined, PhoneOutlined,
  PlusOutlined, TeamOutlined, TrophyOutlined, UserOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../../config';
import { M, G } from '../shared/constants';
import { authHeader, useLang } from '../shared/utils';

const fmtDate = d => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};
const inrFmt = a => a ? `₹${Number(a).toLocaleString('en-IN')}` : '—';
const iS = { width: '100%', padding: '9px 12px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', background: '#fff' };
const lS = { fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 4, display: 'block' };

const SUPPORT_TYPES = [
  { value: 'scholarship', label: 'छात्रवृत्ति (Scholarship)' },
  { value: 'fees',        label: 'फीस सहायता (Fee Support)' },
  { value: 'books',       label: 'पुस्तकें / स्टेशनरी' },
  { value: 'uniform',     label: 'वर्दी / यूनिफॉर्म' },
  { value: 'medical',     label: 'चिकित्सा सहायता' },
  { value: 'other',       label: 'अन्य सहायता' },
];

const STATUS_META = {
  pending:      { label: 'प्रतीक्षारत',  color: '#92400e', bg: '#fffbeb', border: '#fde68a', icon: <LoadingOutlined /> },
  under_review: { label: 'समीक्षाधीन',   color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe', icon: <EyeOutlined /> },
  approved:     { label: 'स्वीकृत',       color: '#15803d', bg: '#f0fdf4', border: '#86efac', icon: <CheckCircleOutlined /> },
  rejected:     { label: 'अस्वीकृत',     color: '#b91c1c', bg: '#fef2f2', border: '#fecaca', icon: <CloseCircleOutlined /> },
  disbursed:    { label: 'वितरित',        color: '#7c3aed', bg: '#faf5ff', border: '#c4b5fd', icon: <TrophyOutlined /> },
};

export function StudentSupportTab() {
  const { t } = useLang();
  const [view,        setView]        = useState('public');  // 'public' | 'mine' | 'apply'
  const [publicList,  setPublicList]  = useState([]);
  const [myList,      setMyList]      = useState([]);
  const [detail,      setDetail]      = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [submitting,  setSubmitting]  = useState(false);
  const [uploading,   setUploading]   = useState(false);
  const [docUrl,      setDocUrl]      = useState('');
  const [sampleUrl,   setSampleUrl]   = useState(null);   // custom form URL from admin
  const docRef = useRef(null);

  const [form, setForm] = useState({
    student_name: '', student_dob: '', student_gender: '', student_class: '',
    school_name: '', school_address: '', father_name: '', mother_name: '',
    family_income: '', family_members: '', village: '', district: '', state: '',
    phone_no: '', support_type: 'scholarship', amount_requested: '', reason: '',
  });
  const F = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const fetchPublic = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${baseUrl}/member/student-support/public`, { headers: authHeader() });
      if (r.data.status) setPublicList(r.data.result || []);
    } catch {} finally { setLoading(false); }
  }, []);

  const fetchMine = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${baseUrl}/member/student-support/my-applications`, { headers: authHeader() });
      if (r.data.status) setMyList(r.data.result || []);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchPublic();
    // Fetch custom sample form URL if admin has uploaded one
    axios.get(`${baseUrl}/member/student-support/sample-form-url`, { headers: authHeader() })
      .then(r => { if (r.data?.url) setSampleUrl(r.data.url); })
      .catch(() => {});
  }, [fetchPublic]);

  const handleDocUpload = async e => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      notification.warning({ message: 'File 10MB से छोटी होनी चाहिए', placement: 'topRight' });
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('application_doc', file);
      const r = await axios.post(`${baseUrl}/member/student-support/apply`, fd, { headers: authHeader() });
      if (r.data.application_doc_url) {
        setDocUrl(r.data.application_doc_url);
        notification.success({ message: 'दस्तावेज़ अपलोड हो गया!', placement: 'topRight' });
      }
    } catch {
      // We'll use a two-step: first upload doc alone, then submit form
      // Actually we'll upload inline with form submission
      notification.info({ message: 'दस्तावेज़ चयनित — फॉर्म भरकर Submit करें', placement: 'topRight' });
    } finally { setUploading(false); }
  };

  // Store selected file (not uploaded yet — upload happens on submit)
  const [selectedFile, setSelectedFile] = useState(null);
  const handleFileSelect = e => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      notification.warning({ message: 'File 10MB से छोटी होनी चाहिए', placement: 'topRight' });
      return;
    }
    setSelectedFile(file);
    notification.success({ message: `✅ ${file.name} चयनित`, placement: 'topRight' });
  };

  const submitForm = async () => {
    if (!selectedFile) {
      notification.error({ message: '⚠️ आवेदन पत्र (document) अपलोड करना अनिवार्य है।', placement: 'topRight' });
      return;
    }
    if (!form.student_name.trim()) { notification.warning({ message: 'विद्यार्थी का नाम जरूरी है', placement: 'topRight' }); return; }
    if (!form.phone_no.trim() || !/^\d{10,12}$/.test(form.phone_no)) { notification.warning({ message: 'सही मोबाइल नंबर (10-12 अंक) जरूरी है', placement: 'topRight' }); return; }
    if (!form.reason.trim() || form.reason.trim().length < 20) { notification.warning({ message: 'कारण कम से कम 20 अक्षर का होना चाहिए', placement: 'topRight' }); return; }
    if (!form.village.trim()) { notification.warning({ message: 'गाँव / शहर जरूरी है', placement: 'topRight' }); return; }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('application_doc', selectedFile);
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      const r = await axios.post(`${baseUrl}/member/student-support/apply`, fd, { headers: authHeader() });
      if (r.data.status) {
        notification.success({ message: r.data.message, placement: 'topRight', duration: 5 });
        setSelectedFile(null);
        setForm({ student_name:'', student_dob:'', student_gender:'', student_class:'', school_name:'', school_address:'', father_name:'', mother_name:'', family_income:'', family_members:'', village:'', district:'', state:'', phone_no:'', support_type:'scholarship', amount_requested:'', reason:'' });
        setView('mine');
        fetchMine();
      } else {
        notification.error({ message: r.data.message, placement: 'topRight' });
      }
    } catch (err) {
      notification.error({ message: err.response?.data?.message || 'Submit failed', placement: 'topRight' });
    } finally { setSubmitting(false); }
  };

  const StatusBadge = ({ status }) => {
    const m = STATUS_META[status] || STATUS_META.pending;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, color: m.color, background: m.bg, border: `1px solid ${m.border}`, borderRadius: 20, padding: '2px 10px' }}>
        {m.icon} {m.label}
      </span>
    );
  };

  const ApplicationCard = ({ a, showDetail = true }) => (
    <div style={{ background: '#fff', borderRadius: 12, border: `1.5px solid ${STATUS_META[a.status]?.border || '#e5e7eb'}`, padding: '13px 14px', marginBottom: 10, boxShadow: '0 1px 5px rgba(0,0,0,0.05)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>{a.student_name}</div>
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
            {[a.student_class, a.school_name].filter(Boolean).join(' · ')}
          </div>
        </div>
        <StatusBadge status={a.status} />
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        {a.support_type && <span style={{ background: `${M}10`, color: M, borderRadius: 20, padding: '2px 8px', fontSize: 10, fontWeight: 600 }}>{SUPPORT_TYPES.find(t => t.value === a.support_type)?.label || a.support_type}</span>}
        {a.village && <span style={{ background: '#f3f4f6', color: '#374151', borderRadius: 20, padding: '2px 8px', fontSize: 10 }}><EnvironmentOutlined style={{ fontSize: 9 }} /> {a.village}{a.district ? `, ${a.district}` : ''}</span>}
        {a.amount_requested && <span style={{ background: '#fffbeb', color: '#92400e', borderRadius: 20, padding: '2px 8px', fontSize: 10, fontWeight: 600 }}>आवेदित: {inrFmt(a.amount_requested)}</span>}
        {a.approved_amount && <span style={{ background: '#f0fdf4', color: '#15803d', borderRadius: 20, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>स्वीकृत: {inrFmt(a.approved_amount)}</span>}
      </div>

      {/* Committee note — always visible (transparency) */}
      {a.committee_note && (
        <div style={{ background: STATUS_META[a.status]?.bg || '#f9fafb', border: `1px solid ${STATUS_META[a.status]?.border || '#e5e7eb'}`, borderRadius: 8, padding: '7px 10px', marginBottom: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', marginBottom: 3 }}>समिति का निर्णय / Committee Note</div>
          <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6 }}>{a.committee_note}</div>
          {a.reviewed_at && <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 3 }}>{fmtDate(a.reviewed_at)}</div>}
        </div>
      )}

      {a.disbursed_at && (
        <div style={{ background: '#faf5ff', border: '1px solid #c4b5fd', borderRadius: 8, padding: '6px 10px', marginBottom: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#7c3aed' }}>✅ सहायता वितरित — {fmtDate(a.disbursed_at)}</div>
          {a.disbursed_note && <div style={{ fontSize: 11, color: '#374151', marginTop: 2 }}>{a.disbursed_note}</div>}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 10, color: '#9ca3af' }}>{fmtDate(a.created_at)}</div>
        {showDetail && a.application_doc && (
          <a href={a.application_doc} target="_blank" rel="noreferrer" style={{ fontSize: 11, fontWeight: 700, color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: 4, textDecoration: 'none' }}>
            <FileTextOutlined /> दस्तावेज़ देखें
          </a>
        )}
      </div>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ background: `linear-gradient(135deg, ${M}, #b71c1c)`, borderRadius: 14, padding: '14px 16px', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <BookOutlined style={{ fontSize: 22, color: '#fff' }} />
          <div>
            <div style={{ fontSize: 15, fontWeight: 900, color: '#fff' }}>{t('शिक्षा सहायता आवेदन', 'Student Support Application')}</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', marginTop: 2 }}>
              {t('समाज के विद्यार्थियों को शिक्षा हेतु आर्थिक सहायता','Financial support for students of our community')}
            </div>
          </div>
        </div>
      </div>

      {/* Nav tabs */}
      <div style={{ display: 'flex', borderBottom: '2px solid #f3f4f6', marginBottom: 14 }}>
        {[
          { k: 'public', l: t('सभी आवेदन','All Applications'), icon: <TeamOutlined /> },
          { k: 'mine',   l: t('मेरे आवेदन','My Applications'), icon: <UserOutlined /> },
          { k: 'apply',  l: t('आवेदन करें','Apply Now'),       icon: <PlusOutlined /> },
        ].map(tab => (
          <button key={tab.k} onClick={() => { setView(tab.k); if (tab.k === 'mine') fetchMine(); }} style={{
            background: 'none', border: 'none', padding: '9px 14px', cursor: 'pointer',
            fontWeight: view === tab.k ? 800 : 500, fontSize: 12,
            color: view === tab.k ? M : '#6b7280',
            borderBottom: view === tab.k ? `2.5px solid ${M}` : '2.5px solid transparent',
            display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
          }}>
            {tab.icon} {tab.l}
          </button>
        ))}
      </div>

      {/* ── ALL APPLICATIONS (public transparency view) ── */}
      {view === 'public' && (
        <div>
          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '9px 13px', marginBottom: 12, display: 'flex', gap: 8 }}>
            <InfoCircleOutlined style={{ color: '#1d4ed8', flexShrink: 0, marginTop: 1 }} />
            <div style={{ fontSize: 11, color: '#1e40af', lineHeight: 1.6 }}>
              समाज की पारदर्शिता के लिए सभी आवेदन यहाँ दिखाई देते हैं। समिति के निर्णय और कारण सार्वजनिक हैं।
            </div>
          </div>
          {loading && <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>}
          {!loading && publicList.length === 0 && (
            <div style={{ textAlign: 'center', padding: 48, color: '#9ca3af' }}>
              <BookOutlined style={{ fontSize: 36, marginBottom: 10, display: 'block', color: '#d1d5db' }} />
              <div style={{ fontSize: 13 }}>अभी कोई आवेदन नहीं है।</div>
            </div>
          )}
          {!loading && publicList.map(a => <ApplicationCard key={a.id} a={a} />)}
        </div>
      )}

      {/* ── MY APPLICATIONS ── */}
      {view === 'mine' && (
        <div>
          {loading && <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>}
          {!loading && myList.length === 0 && (
            <div style={{ textAlign: 'center', padding: 48, color: '#9ca3af' }}>
              <FileTextOutlined style={{ fontSize: 36, marginBottom: 10, display: 'block', color: '#d1d5db' }} />
              <div style={{ fontSize: 13, marginBottom: 14 }}>आपने अभी कोई आवेदन नहीं किया।</div>
              <button onClick={() => setView('apply')} style={{ background: M, color: G, border: 'none', borderRadius: 9, padding: '9px 20px', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>
                + आवेदन करें
              </button>
            </div>
          )}
          {!loading && myList.map(a => <ApplicationCard key={a.id} a={a} />)}
        </div>
      )}

      {/* ── APPLY FORM ── */}
      {view === 'apply' && (
        <div>
          {/* Step 1: Download sample */}
          <div style={{ background: 'linear-gradient(135deg,#fffbeb,#fef3c7)', border: '1.5px solid #fde68a', borderRadius: 12, padding: '14px 16px', marginBottom: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#92400e', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
              <DownloadOutlined /> चरण 1: नमूना आवेदन पत्र डाउनलोड करें
            </div>
            <div style={{ fontSize: 11, color: '#78350f', marginBottom: 10, lineHeight: 1.6 }}>
              नीचे दिया गया नमूना पत्र डाउनलोड करें → भरें → स्कैन/फोटो लें → नीचे Upload करें।
              <br />दस्तावेज़ अपलोड किए बिना आवेदन जमा नहीं होगा।
            </div>
            <a
              href={sampleUrl || `${baseUrl}/member/student-support/sample-form`}
              target="_blank" rel="noreferrer"
              download={sampleUrl ? true : undefined}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#92400e', color: '#fff', borderRadius: 8, padding: '8px 16px', fontSize: 12, fontWeight: 700, textDecoration: 'none' }}
            >
              <DownloadOutlined /> नमूना पत्र डाउनलोड करें
            </a>
          </div>

          {/* Step 2: Upload document (MANDATORY) */}
          <div style={{ background: selectedFile ? '#f0fdf4' : '#fef2f2', border: `2px dashed ${selectedFile ? '#86efac' : '#fca5a5'}`, borderRadius: 12, padding: '16px', marginBottom: 14, textAlign: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: selectedFile ? '#15803d' : '#b91c1c', marginBottom: 6 }}>
              {selectedFile ? `✅ ${selectedFile.name}` : '⚠️ चरण 2: भरा हुआ आवेदन पत्र अपलोड करें (अनिवार्य)'}
            </div>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 10 }}>
              {selectedFile ? 'दस्तावेज़ चयनित — फॉर्म भरकर Submit करें' : 'PDF, JPG, PNG — अधिकतम 10MB · बिना document के Submit नहीं होगा'}
            </div>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: selectedFile ? '#15803d' : M, color: '#fff', borderRadius: 8, padding: '8px 18px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              <CloudUploadOutlined /> {selectedFile ? 'बदलें' : 'दस्तावेज़ चुनें'}
              <input ref={docRef} type="file" accept="image/*,.pdf,.doc,.docx" style={{ display: 'none' }} onChange={handleFileSelect} />
            </label>
          </div>

          {/* Form */}
          <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', padding: '18px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: M, marginBottom: 16 }}>
              <FileTextOutlined style={{ marginRight: 7 }} /> चरण 3: आवेदन विवरण भरें
            </div>

            {[
              {
                title: 'विद्यार्थी की जानकारी',
                fields: [
                  { col:'1/-1', label:'विद्यार्थी का पूरा नाम *', key:'student_name', ph:'पूरा नाम' },
                  { label:'जन्म तिथि', key:'student_dob', type:'date' },
                  { label:'लिंग', key:'student_gender', type:'select', opts:[['','चुनें'],['Male','पुरुष'],['Female','महिला'],['Other','अन्य']] },
                  { label:'कक्षा / Course', key:'student_class', ph:'जैसे: कक्षा 10, B.A. 1st Year' },
                  { label:'विद्यालय / महाविद्यालय का नाम', key:'school_name', ph:'School / College name' },
                  { col:'1/-1', label:'विद्यालय का पता', key:'school_address', ph:'पता' },
                ],
              },
              {
                title: 'परिवार की जानकारी',
                fields: [
                  { label:'पिता का नाम', key:'father_name', ph:'पिता का नाम' },
                  { label:'माता का नाम', key:'mother_name', ph:'माता का नाम' },
                  { label:'अभिभावक का नाम (यदि अलग हो)', key:'guardian_name' },
                  { label:'वार्षिक पारिवारिक आय (₹)', key:'family_income', type:'number', ph:'जैसे: 60000' },
                  { label:'परिवार में सदस्य संख्या', key:'family_members', type:'number', ph:'जैसे: 5' },
                  { label:'मोबाइल नंबर *', key:'phone_no', type:'tel', ph:'10 अंक का नंबर' },
                  { label:'गाँव / शहर *', key:'village', ph:'गाँव / शहर' },
                  { label:'जिला', key:'district', ph:'जिला' },
                  { label:'राज्य', key:'state', ph:'राज्य' },
                ],
              },
              {
                title: 'सहायता का विवरण',
                fields: [
                  { label:'सहायता का प्रकार *', key:'support_type', type:'select', opts:SUPPORT_TYPES.map(t => [t.value, t.label]) },
                  { label:'आवश्यक राशि (₹)', key:'amount_requested', type:'number', ph:'जैसे: 5000' },
                  { col:'1/-1', label:'सहायता की आवश्यकता का कारण * (कम से कम 20 अक्षर)', key:'reason', type:'textarea', ph:'विस्तार से बताएं कि आपको सहायता की क्यों आवश्यकता है…' },
                ],
              },
            ].map(section => (
              <div key={section.title} style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: M, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10, paddingBottom: 5, borderBottom: `1px solid ${M}20` }}>
                  {section.title}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 14px' }}>
                  {section.fields.map(field => (
                    <div key={field.key} style={{ gridColumn: field.col || 'auto' }}>
                      <label style={lS}>{field.label}</label>
                      {field.type === 'select' ? (
                        <select style={iS} value={form[field.key]} onChange={F(field.key)}>
                          {field.opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                      ) : field.type === 'textarea' ? (
                        <textarea style={{ ...iS, height: 80, resize: 'vertical' }} value={form[field.key]} onChange={F(field.key)} placeholder={field.ph || ''} maxLength={2000} />
                      ) : (
                        <input type={field.type || 'text'} style={iS} value={form[field.key]} onChange={F(field.key)} placeholder={field.ph || ''} maxLength={300} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Submit */}
            {!selectedFile && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, fontWeight: 700, color: '#b91c1c', display: 'flex', alignItems: 'center', gap: 7 }}>
                <InfoCircleOutlined /> ऊपर दिया गया आवेदन पत्र अपलोड करें — Submit बटन तभी काम करेगा।
              </div>
            )}

            <button
              onClick={submitForm}
              disabled={submitting || !selectedFile}
              style={{
                width: '100%', background: submitting || !selectedFile ? '#9ca3af' : M,
                color: '#fff', border: 'none', borderRadius: 10, padding: '13px 0',
                fontWeight: 800, fontSize: 14, cursor: submitting || !selectedFile ? 'not-allowed' : 'pointer',
              }}
            >
              {submitting ? <Spin size="small" /> : selectedFile ? '✅ आवेदन जमा करें' : '📄 पहले दस्तावेज़ अपलोड करें'}
            </button>

            <div style={{ marginTop: 10, fontSize: 11, color: '#9ca3af', textAlign: 'center', lineHeight: 1.6 }}>
              आवेदन जमा होने के बाद समिति समीक्षा करेगी। स्थिति "मेरे आवेदन" में दिखेगी।
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
